import {UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {Nullish} from 'shared/web';

import {isTransmissionExcluded} from '../../../../../constants/transmissionOptionsExcluded';
import {
  NumericBoundBody,
  RelativeFilterChoice,
  RelativeFilterConfigBody,
} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {getSimilarBodyStyles} from '../../../../../utils/getSimilarBodyStyles';
import {getBetterOrEqualValues} from './getBetterOrEqualValues';

/**
 * Resolves one end of a numeric bound to an absolute filter value.
 *
 * Convention: BE stores offsets as signed values relative to the vehicle:
 *   from: negative means "vehicle minus N"  e.g. from = -10000 means ≥ vehicle−10000
 *   to:   positive means "vehicle plus N"   e.g. to   = +10000 means ≤ vehicle+10000
 *   from === 0 or to === 0 means "same as vehicle"
 *   null means "no constraint on this end"
 */
function resolveEnd(signedOffset: number | null, base: number, unit: 'abs' | 'pct'): number | null {
  if (isNil(signedOffset)) {
    return null;
  }
  if (unit === 'pct') {
    return Math.round(base * (1 + signedOffset / 100));
  }
  return Math.round(base + signedOffset);
}

/**
 * Picks the active bound (abs vs pct) and resolves both ends.
 * If only one of abs/pct is non-null that side wins; abs takes precedence if both are set.
 */
function resolveBoundPair(
  abs: NumericBoundBody | null,
  pct: NumericBoundBody | null,
  base: number,
  priceBase?: number
): {gte: number | null; lte: number | null} {
  const active = abs ?? pct;
  if (isNil(active)) {
    return {gte: null, lte: null};
  }
  const unit: 'abs' | 'pct' = isNotNil(abs) ? 'abs' : 'pct';
  const effectiveBase = unit === 'pct' ? (priceBase ?? base) : base;
  return {
    gte: resolveEnd(active.from, effectiveBase, unit),
    lte: resolveEnd(active.to, effectiveBase, unit),
  };
}

function resolveSingleBound(
  bound: NumericBoundBody | null,
  base: number | Nullish
): {gte: number | null; lte: number | null} {
  if (isNil(bound)) {
    return {gte: null, lte: null};
  }
  const b = base ?? 0;
  return {
    gte: resolveEnd(bound.from, b, 'abs'),
    lte: resolveEnd(bound.to, b, 'abs'),
  };
}

function resolveThreeWay(mode: RelativeFilterChoice | null): 'any' | 'same' | 'same_better' {
  if (isNil(mode)) {
    return 'any';
  }
  return mode;
}

/**
 * Applies a relative-filter preset config to the existing filter form.
 * Pure function — calls formApi.setValue for each relevant field, then calls onChangeCallback.
 */
export function applyPresetToForm(
  config: RelativeFilterConfigBody,
  priceReport: PriceReportType,
  formApi: UseFormReturn<FilterFormData>,
  onChangeCallback: () => void
): void {
  const vehicle = priceReport.vehicle;
  const recommendedPrice = priceReport.sellingPrice.recommended.value;
  const {setValue} = formApi;

  // --- Year ---
  const {gte: yearGte, lte: yearLte} = resolveSingleBound(
    config.year,
    vehicle.firstRegistrationOnYear
  );
  setValue('firstRegistrationOnYearGte', yearGte);
  setValue('firstRegistrationOnYearLte', yearLte);

  // --- Mileage ---
  const {gte: mileageGte, lte: mileageLte} = resolveBoundPair(
    config.mileage,
    config.mileagePct,
    vehicle.mileage ?? 0
  );
  setValue('mileageGte', mileageGte);
  setValue('mileageLte', mileageLte);

  // --- Power ---
  const vehiclePower = vehicle.maxPower ?? vehicle.power ?? 0;
  const {gte: powerGte, lte: powerLte} = resolveBoundPair(
    config.power,
    config.powerPct,
    vehiclePower
  );
  setValue('powerGte', powerGte);
  setValue('powerLte', powerLte);

  // --- Price ---
  const {gte: priceGte, lte: priceLte} = resolveBoundPair(
    config.price,
    config.pricePct,
    recommendedPrice ?? 0,
    recommendedPrice ?? 0
  );
  setValue('priceGte', priceGte);
  setValue('priceLte', priceLte);

  // --- Feature score ---
  const {gte: featureGte, lte: featureLte} = resolveSingleBound(
    config.featureScore,
    priceReport.featuresLevel
  );
  setValue('featureScoreGte', featureGte);
  setValue('featureScoreLte', featureLte);

  // --- Radius ---
  setValue('radius', config.radius);

  // --- Transmission ---
  const transmissionMode = resolveThreeWay(config.transmission);
  if (transmissionMode === 'any') {
    setValue('transmission', []);
  } else if (transmissionMode === 'same_better') {
    setValue(
      'transmission',
      getBetterOrEqualValues('transmission', vehicle.transmission).filter(
        (t) => !isTransmissionExcluded(t)
      )
    );
  } else {
    setValue(
      'transmission',
      isTransmissionExcluded(vehicle.transmission) ? [] : [vehicle.transmission]
    );
  }

  // --- Fuel ---
  setValue('fuelType', config.fuel === 'same' ? [vehicle.fuelType] : []);

  // --- Body style ---
  setValue('bodyStyle', config.body === 'same' ? getSimilarBodyStyles(vehicle.bodyStyle) : []);

  // --- Drive ---
  const driveMode = resolveThreeWay(config.drive);
  if (driveMode === 'any') {
    setValue('drive', []);
  } else if (driveMode === 'same_better') {
    setValue('drive', getBetterOrEqualValues('drive', vehicle.drive));
  } else {
    setValue('drive', [vehicle.drive]);
  }

  // --- VAT ---
  const vatMode = resolveThreeWay(config.vatDeductible);
  const vehicleVat: 'YES' | 'NO' = vehicle.isVatDeductible === true ? 'YES' : 'NO';
  if (vatMode === 'any') {
    setValue('isVatDeductible', []);
  } else if (vatMode === 'same_better') {
    const isVatValue = (v: string): v is 'YES' | 'NO' => v === 'YES' || v === 'NO';
    setValue('isVatDeductible', getBetterOrEqualValues('vat', vehicleVat).filter(isVatValue));
  } else {
    setValue('isVatDeductible', [vehicleVat]);
  }

  // --- Seller type ---
  setValue('sellerType', []);

  // --- Trim levels ---
  const trimMode = resolveThreeWay(config.trimLevel);
  if (trimMode === 'any') {
    setValue('trimLevels', []);
  } else {
    setValue('trimLevels', isNotNil(vehicle.trimLevel) ? [vehicle.trimLevel] : []);
  }

  // --- Seats ---
  // TODO: add seats field to FilterFormData when backend alignment is confirmed.

  onChangeCallback();
}
